import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';

import { dateTimeFormatted, toDate } from '../../datetime';
import { globalStyles } from '../../theme';
import './styles.css';

interface IProductLineChange {
  description: string;
  code: string;
  supplierName: string;
  price: string;
  quantity: string;
  purchaseOrderNumber: string;
}

export interface IAuditEvent {
  action: string;
  date: string;
  repCode: string;
  payload?: {
    orderChanges: Array<{ name: string; oldValue: string; newValue: string }>;
    productChanges: {
      deletes: IProductLineChange[];
      adds: IProductLineChange[];
      modified: IProductLineChange[];
    };
    comment?: string;
  };
}

export interface IAuditEventLogProps {
  events: IAuditEvent[];
  describeEvent: (event: IAuditEvent) => string;
}

interface IStyledAuditEventLogProps
  extends IAuditEventLogProps,
    WithStyles<typeof styles> {}

const ModifiedEventRow: React.SFC<{ event: IAuditEvent }> = ({ event }) => {
  const payload = event.payload;
  return (
    <div className="event-item">
      <Grid container>
        <Grid item xs={6}>
          {`Modified by ${event.repCode}`}
          {payload &&
            payload.orderChanges.length > 0 &&
            payload.orderChanges.map((change) => (
              <Grid key={change.name} item xs={12}>
                <span>{change.name}: </span>
                <span style={{ textDecoration: 'line-through' }}>{change.oldValue}</span>
                <span> - {change.newValue}</span>
              </Grid>
            ))}
          {payload && payload.productChanges.adds.length > 0 && (
            <Grid style={{ paddingTop: '5px' }} item xs={12}>
              Added Product Lines
            </Grid>
          )}
          {payload &&
            payload.productChanges.adds.length > 0 &&
            payload.productChanges.adds.map((change) => (
              <Grid style={{ paddingLeft: '2px' }} key={change.code} item xs={12}>
                <span>
                  {change.description}, {change.code}, {change.supplierName}, {change.price}, {change.quantity},{' '}
                  {change.purchaseOrderNumber}{' '}
                </span>
              </Grid>
            ))}
          {payload && payload.productChanges.deletes.length > 0 && (
            <Grid style={{ paddingTop: '5px' }} item xs={12}>
              Deleted Product Lines
            </Grid>
          )}
          {payload &&
            payload.productChanges.deletes.length > 0 &&
            payload.productChanges.deletes.map((change) => (
              <Grid style={{ paddingLeft: '2px' }} key={change.code} item xs={12}>
                <span style={{ textDecoration: 'line-through' }}>
                  {change.description}, {change.code}, {change.supplierName}, {change.price}, {change.quantity},{' '}
                  {change.purchaseOrderNumber}{' '}
                </span>
              </Grid>
            ))}
          {payload && payload.productChanges.modified.length > 0 && (
            <Grid style={{ paddingTop: '5px' }} item xs={12}>
              Modified Product Lines
            </Grid>
          )}
          {payload &&
            payload.productChanges.modified.length > 0 &&
            payload.productChanges.modified.map((change) => (
              <Grid style={{ paddingLeft: '2px' }} key={change.code} item xs={12}>
                <span>
                  {change.description}, {change.code}, {change.supplierName}, {change.price}, {change.quantity},{' '}
                  {change.purchaseOrderNumber}{' '}
                </span>
              </Grid>
            ))}
        </Grid>
        <Grid item xs={6}>
          {dateTimeFormatted(toDate(event.date))}
        </Grid>
      </Grid>
    </div>
  );
};

const EventRow: React.SFC<{
  event: IAuditEvent;
  describeEvent: (event: IAuditEvent) => string;
}> = ({ event, describeEvent }) => (
  <div className="event-item">
    <Grid container>
      <Grid item xs={6}>
        {describeEvent(event)}
      </Grid>
      <Grid item xs={6}>
        {dateTimeFormatted(toDate(event.date))}
      </Grid>
    </Grid>
  </div>
);

const AuditEventLog: React.SFC<IStyledAuditEventLogProps> = ({
  classes,
  describeEvent,
  events,
}) => (
  <ExpansionPanel className={classes.expansion}>
    <ExpansionPanelSummary
      expandIcon={<ExpandMoreIcon />}
      className={classes.expansionSection}
    >
      Event log
    </ExpansionPanelSummary>
    <ExpansionPanelDetails className={classes.expansionSection}>
      <div className="event-items">
        {events.map((event, index) =>
          event.action === 'Modified' ? (
            <ModifiedEventRow event={event} key={index} />
          ) : (
            <EventRow
              event={event}
              describeEvent={describeEvent}
              key={index}
            />
          ),
        )}
      </div>
    </ExpansionPanelDetails>
  </ExpansionPanel>
);

const styles = (theme: Theme) =>
  createStyles({
    ...globalStyles,
    expansion: {
      boxShadow: 'none',
      marginBottom: 30,
    },
    expansionSection: {
      padding: 0,
    },
  });

export default withStyles(styles)(AuditEventLog);
